import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 16px;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease-out;
  position: relative;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 14px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: #fafbfc;

  &:focus {
    outline: none;
    border-color: #4a90e2;
    background-color: white;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #c1c9d2;
    background-color: white;
  }

  &::placeholder {
    color: #8b95a1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const AuthButton = styled.button`
  background: linear-gradient(135deg, #4a90e2 0%, #357ae8 100%);
  color: white;
  border: none;
  flex: 1;
  border-radius: 16px;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(74, 144, 226, 0.2);
  position: relative;
  overflow: hidden;

  &:hover {
    background: linear-gradient(135deg, #357ae8 0%, #2968d4 100%);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.3), 0 4px 12px rgba(74, 144, 226, 0.3);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(74, 144, 226, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #6c757d;
  border: 2px solid #e1e5e9;
  flex: 1;
  border-radius: 16px;
  padding: 10px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background-color: #f8f9fa;
    border-color: #c1c9d2;
    color: #495057;
  }

  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }

  &:active {
    background-color: #e9ecef;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: -8px;
  padding: 8px 12px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  animation: shake 0.5s ease-in-out;

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    75% { transform: translateX(4px); }
  }
`;

const SuccessMessage = styled.div`
  color: #155724;
  font-size: 14px;
  margin-top: -8px;
  padding: 8px 12px;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 24px;
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 4px;
`;

const Tab = styled.div<{ active: boolean }>`
  padding: 12px 20px;
  cursor: pointer;
  border-radius: 6px;
  color: ${props => props.active ? 'white' : '#6c757d'};
  font-weight: ${props => props.active ? '600' : '500'};
  background-color: ${props => props.active ? '#4a90e2' : 'transparent'};
  transition: all 0.3s ease;
  flex: 1;
  text-align: center;
  font-size: 14px;

  &:hover {
    color: ${props => props.active ? 'white' : '#495057'};
    background-color: ${props => props.active ? '#4a90e2' : '#e9ecef'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.3);
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #4a90e2;
  cursor: pointer;
  font-size: 14px;
  flex: 1;
  text-decoration: none;
  padding: 8px 0;
  transition: all 0.3s ease;
  border-radius: 4px;

  &:hover {
    color: #357ae8;
    background-color: rgba(74, 144, 226, 0.05);
    text-decoration: underline;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.3);
    background-color: rgba(74, 144, 226, 0.05);
  }
`;

const LoadingMessage = styled.div`
  color: #4a90e2;
  text-align: center;
  margin-bottom: 4px;
  font-weight: 500;
`;

enum AuthMode {
  LOGIN = 'login',
  REGISTER = 'register',
  RESET_PASSWORD = 'reset',
}

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authMode, setAuthMode] = useState<AuthMode>(AuthMode.LOGIN);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCloseModal = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      handleCloseModal();
    } catch (error) {
      setError(t('auth.errors.googleLoginFailed'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    // Basic validation
    if (!email) {
      setError(t('auth.errors.emailRequired'));
      setIsSubmitting(false);
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError(t('auth.errors.invalidEmail'));
      setIsSubmitting(false);
      return;
    }
    if (!password) {
      setError(t('auth.errors.passwordRequired'));
      setIsSubmitting(false);
      return;
    }
    if (password.length < 6) {
      setError(t('auth.errors.passwordMinLength'));
      setIsSubmitting(false);
      return;
    }

    try {
      if (authMode === AuthMode.LOGIN) {
        const { error } = await signInWithEmail(email, password);
        if (error) {
          setError(error.message || t('auth.errors.loginFailed'));
        } else {
          setSuccess(t('auth.success.loginSuccess'));
          setTimeout(() => {
            handleCloseModal();
          }, 2000);
        }
      } else if (authMode === AuthMode.REGISTER) {
        if (password !== confirmPassword) {
          setError(t('auth.errors.passwordsDontMatch'));
          setIsSubmitting(false);
          return;
        }
        const { error } = await signUpWithEmail(email, password);
        if (error) {
          setError(error.message || t('auth.errors.registerFailed'));
        } else {
          setSuccess(t('auth.success.registerSuccess'));
          setAuthMode(AuthMode.LOGIN);
        }
      }
    } catch (err) {
      setError(t('auth.errors.genericError'));
    }
    setIsSubmitting(false);
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError(t('auth.errors.emailRequired'));
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError(t('auth.errors.invalidEmail'));
      return;
    }

    try {
      const { error } = await resetPassword(email);
      if (error) {
        setError(error.message || t('auth.errors.resetPasswordFailed'));
      } else {
        setSuccess(t('auth.success.resetPasswordSent'));
        setAuthMode(AuthMode.LOGIN);
      }
    } catch (error) {
      setError(t('auth.errors.resetPasswordFailed'));
    }
  };

  if (!isOpen) return null;

  return (
    <Modal onClick={handleCloseModal}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <TabContainer>
          <Tab 
            active={authMode === AuthMode.LOGIN} 
            onClick={() => {
              setAuthMode(AuthMode.LOGIN);
              setError(null);
              setSuccess(null);
            }}
          >
            {t('auth.login')}
          </Tab>
          <Tab 
            active={authMode === AuthMode.REGISTER} 
            onClick={() => {
              setAuthMode(AuthMode.REGISTER);
              setError(null);
              setSuccess(null);
            }}
          >
            {t('auth.register')}
          </Tab>
        </TabContainer>

        <Form onSubmit={handleSubmit}>
          <Input 
            type="email" 
            placeholder={t('auth.email')} 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          
          {authMode !== AuthMode.RESET_PASSWORD && (
            <Input 
              type="password" 
              placeholder={t('auth.password')} 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
          
          {authMode === AuthMode.REGISTER && (
            <Input 
              type="password" 
              placeholder={t('auth.confirmPassword')} 
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          )}
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          
          {isSubmitting && (authMode === AuthMode.LOGIN || authMode === AuthMode.REGISTER) && (
            <LoadingMessage>
              {authMode === AuthMode.LOGIN
                ? t('auth.loginLoading')
                : t('auth.registerLoading')}
            </LoadingMessage>
          )}
          <ButtonGroup>
            <SecondaryButton type="button" onClick={handleCloseModal} disabled={isSubmitting}>
              {t('game.cancel')}
            </SecondaryButton>
            <AuthButton type="submit" disabled={isSubmitting}>
              {authMode === AuthMode.LOGIN &&  t('auth.login')}
              {authMode === AuthMode.REGISTER && t('auth.register')}
              {authMode === AuthMode.RESET_PASSWORD && t('auth.sendResetLink')}
            </AuthButton>
          </ButtonGroup>
          
          {authMode === AuthMode.RESET_PASSWORD && (
            <LinkButton 
              type="button" 
              onClick={() => {
                setAuthMode(AuthMode.LOGIN);
                setError(null);
                setSuccess(null);
              }}
            >
              {t('auth.backToLogin')}
            </LinkButton>
          )}
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default AuthModal; 